import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const TAG_COLORS = {
  gold: "bg-yellow-100 text-yellow-800 border-yellow-300",
  bad: "bg-red-100 text-red-800 border-red-300",
  skip: "bg-zinc-100 text-zinc-600 border-zinc-300",
};

const TIERS = ["individual", "leader", "pl_owner"];
const SCORE_DIMS = [
  "signal_read_clarity",
  "operator_coach_voice",
  "commercial_specificity",
  "seniority_register_fit",
  "distinctiveness",
];

function CheckBadge({ name, result }) {
  const pass = result?.failed === 0;
  return (
    <div
      data-testid={`check-badge-${name}`}
      className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-mono border ${
        pass
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : "bg-red-50 text-red-700 border-red-200"
      }`}
    >
      <span>{pass ? "✓" : "✗"}</span>
      <span>{name.replace(/_/g, " ")}</span>
      {!pass && (
        <span className="ml-0.5 font-semibold">{result?.failed}</span>
      )}
    </div>
  );
}

function ScorePicker({ dim, value, onChange }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs text-zinc-500 w-44 shrink-0">{dim.replace(/_/g, " ")}</span>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            data-testid={`score-${dim}-${n}`}
            onClick={() => onChange(n)}
            className={`w-7 h-7 rounded text-xs font-medium border transition-colors ${
              value === n
                ? "bg-zinc-900 text-white border-zinc-900"
                : "bg-white text-zinc-400 border-zinc-200 hover:border-zinc-400"
            }`}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

function CardScoringPanel({ card, cardIdx, tier, scores, onScoreChange, onNotesChange, notes }) {
  return (
    <div className="border border-zinc-200 rounded-lg p-4 space-y-3 bg-white">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-mono text-zinc-400 uppercase tracking-wide">{tier} · card {cardIdx + 1}</p>
          <p className="text-sm font-semibold text-zinc-900 mt-0.5 leading-snug">{card.title}</p>
          {card.card_job_type && (
            <span className="inline-block mt-1 text-[11px] font-mono bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded">
              {card.card_job_type}
            </span>
          )}
        </div>
        {card._plan_committed_jtbd && (
          <span className="shrink-0 text-[10px] font-mono bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded border border-indigo-200">
            plan-path
          </span>
        )}
      </div>

      {card.body_markdown && (
        <details className="text-xs text-zinc-500 leading-relaxed">
          <summary className="cursor-pointer text-zinc-400 hover:text-zinc-700">Show body</summary>
          <p className="mt-2 whitespace-pre-wrap">{card.body_markdown}</p>
        </details>
      )}

      <div className="space-y-1.5 pt-1 border-t border-zinc-100">
        {SCORE_DIMS.map((dim) => (
          <ScorePicker
            key={dim}
            dim={dim}
            value={scores?.[dim] ?? null}
            onChange={(v) => onScoreChange(cardIdx, dim, v)}
          />
        ))}
      </div>

      <textarea
        data-testid={`card-notes-${cardIdx}`}
        className="w-full text-xs text-zinc-700 border border-zinc-200 rounded px-2.5 py-1.5 resize-none h-16 placeholder-zinc-400 focus:outline-none focus:border-zinc-400"
        placeholder="Notes (optional)"
        value={notes || ""}
        onChange={(e) => onNotesChange(cardIdx, e.target.value)}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Reveal detail panel
// ─────────────────────────────────────────────────────────────────────────────

function RevealDetailPanel({ revealId, accountName, generationCycle, onClose, onTagged }) {
  const [detail, setDetail] = useState(null);
  const [checks, setChecks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checksLoading, setChecksLoading] = useState(false);
  const [activeTag, setActiveTag] = useState(null);
  const [cardScores, setCardScores] = useState({});   // cardIdx → {dim: score}
  const [cardNotes, setCardNotes] = useState({});     // cardIdx → string
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await API.get(`/eval/reveal/${revealId}`);
        setDetail(data);
        setActiveTag(data.tag || null);
        // Re-hydrate existing scores
        if (data.card_scores?.length) {
          const sc = {};
          const nt = {};
          data.card_scores.forEach((cs) => {
            if (cs.human_scores) sc[cs.card_idx] = cs.human_scores;
            if (cs.notes) nt[cs.card_idx] = cs.notes;
          });
          setCardScores(sc);
          setCardNotes(nt);
        }
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, [revealId]);

  const runChecks = useCallback(async () => {
    setChecksLoading(true);
    try {
      const { data } = await API.get(`/eval/automated-checks`, {
        params: { reveal_id: revealId },
      });
      setChecks(data);
    } catch {
      // ignore
    } finally {
      setChecksLoading(false);
    }
  }, [revealId]);

  const handleScoreChange = (cardIdx, dim, value) => {
    setCardScores((prev) => ({
      ...prev,
      [cardIdx]: { ...(prev[cardIdx] || {}), [dim]: value },
    }));
  };

  const handleNotesChange = (cardIdx, notes) => {
    setCardNotes((prev) => ({ ...prev, [cardIdx]: notes }));
  };

  const handleTag = async (tag) => {
    try {
      await API.post("/eval/tag", {
        reveal_id: revealId,
        account_name: accountName,
        generation_cycle: generationCycle,
        tag,
      });
      setActiveTag(tag);
      onTagged?.(revealId, tag);
      setSaveMsg(`Tagged as ${tag}`);
      setTimeout(() => setSaveMsg(""), 3000);
    } catch {
      setSaveMsg("Error saving tag");
    }
  };

  const handleSaveScores = async () => {
    setSaving(true);
    try {
      const allCards = detail?.cards || [];
      const cardScoresPayload = allCards.map((card, idx) => ({
        card_idx: idx,
        title: card.title || "",
        tier: card._tier || "",
        card_job_type: card.card_job_type || "",
        human_scores: cardScores[idx] || null,
        notes: cardNotes[idx] || "",
      }));
      await API.post("/eval/score", {
        reveal_id: revealId,
        account_name: accountName,
        generation_cycle: generationCycle,
        card_scores: cardScoresPayload,
      });
      setSaveMsg("Scores saved");
      setTimeout(() => setSaveMsg(""), 3000);
    } catch {
      setSaveMsg("Error saving scores");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
        <div className="bg-white rounded-xl p-8 text-zinc-500 text-sm">Loading…</div>
      </div>
    );
  }

  const cardsByTier = {};
  (detail?.cards || []).forEach((card, idx) => {
    const t = card._tier || "unknown";
    if (!cardsByTier[t]) cardsByTier[t] = [];
    cardsByTier[t].push({ ...card, _globalIdx: idx });
  });

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6">
      <div className="bg-white w-full sm:max-w-4xl sm:rounded-2xl shadow-2xl flex flex-col max-h-screen sm:max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100 shrink-0">
          <div>
            <p className="font-semibold text-zinc-900">{accountName}</p>
            <p className="text-xs text-zinc-400 font-mono">{generationCycle}</p>
          </div>
          <div className="flex items-center gap-2">
            {saveMsg && (
              <span className="text-xs text-emerald-600 font-medium">{saveMsg}</span>
            )}
            <button
              data-testid="close-reveal-detail"
              onClick={onClose}
              className="text-zinc-400 hover:text-zinc-700 text-lg leading-none px-2"
            >
              ×
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-6">
          {/* Tag buttons */}
          <div>
            <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">
              Tag reveal
            </p>
            <div className="flex gap-2">
              {["gold", "bad", "skip"].map((t) => (
                <button
                  key={t}
                  data-testid={`tag-btn-${t}`}
                  onClick={() => handleTag(t)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-all ${
                    activeTag === t
                      ? TAG_COLORS[t] + " ring-2 ring-offset-1 ring-current"
                      : "bg-white text-zinc-400 border-zinc-200 hover:border-zinc-400"
                  }`}
                >
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Automated checks */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">
                Automated checks
              </p>
              <Button
                data-testid="run-checks-btn"
                size="sm"
                variant="outline"
                onClick={runChecks}
                disabled={checksLoading}
                className="text-xs h-7"
              >
                {checksLoading ? "Running…" : checks ? "Re-run" : "Run checks"}
              </Button>
            </div>
            {checks ? (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  {Object.entries(checks.checks).map(([name, result]) => (
                    <CheckBadge key={name} name={name} result={result} />
                  ))}
                </div>
                {!checks.overall_pass && (
                  <p className="text-xs text-red-600 font-mono">{checks.summary}</p>
                )}
              </div>
            ) : (
              <p className="text-xs text-zinc-400">Click "Run checks" to inspect this reveal.</p>
            )}
          </div>

          {/* Card scoring */}
          <div>
            <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-3">
              Card scores
            </p>
            {TIERS.map((tier) => {
              const tCards = cardsByTier[tier] || [];
              if (!tCards.length) return null;
              return (
                <div key={tier} className="mb-5">
                  <p className="text-xs font-mono text-zinc-400 uppercase mb-2">
                    {tier.replace("_", " ")}
                  </p>
                  <div className="space-y-3">
                    {tCards.map((card, localIdx) => (
                      <CardScoringPanel
                        key={localIdx}
                        card={card}
                        cardIdx={card._globalIdx}
                        tier={tier}
                        scores={cardScores[card._globalIdx]}
                        notes={cardNotes[card._globalIdx]}
                        onScoreChange={handleScoreChange}
                        onNotesChange={handleNotesChange}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 px-6 py-4 border-t border-zinc-100 flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose} className="text-sm" data-testid="cancel-scoring-btn">
            Cancel
          </Button>
          <Button
            data-testid="save-scores-btn"
            onClick={handleSaveScores}
            disabled={saving}
            className="text-sm bg-zinc-900 text-white hover:bg-zinc-700"
          >
            {saving ? "Saving…" : "Save scores"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────

export default function AdminEvalPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [reveals, setReveals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accountFilter, setAccountFilter] = useState("");
  const [selectedReveal, setSelectedReveal] = useState(null);
  const [tagCounts, setTagCounts] = useState({ gold: 0, bad: 0, skip: 0 });
  const [exporting, setExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState("");

  useEffect(() => {
    if (user && !user.is_admin) navigate("/intelligence");
  }, [user, navigate]);

  const loadReveals = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (accountFilter.trim()) params.account = accountFilter.trim();
      const { data } = await API.get("/eval/reveals", { params });
      setReveals(data.reveals || []);

      // Update tag counts
      const counts = { gold: 0, bad: 0, skip: 0 };
      (data.reveals || []).forEach((r) => {
        if (r.tag) counts[r.tag] = (counts[r.tag] || 0) + 1;
      });
      setTagCounts(counts);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [accountFilter]);

  useEffect(() => {
    loadReveals();
  }, [loadReveals]);

  const handleTagged = (revealId, tag) => {
    setReveals((prev) =>
      prev.map((r) => (r.reveal_id === revealId ? { ...r, tag } : r))
    );
    setTagCounts((prev) => {
      const next = { ...prev };
      next[tag] = (next[tag] || 0) + 1;
      return next;
    });
  };

  const handleExport = async () => {
    setExporting(true);
    setExportMsg("");
    try {
      const { data } = await API.post("/eval/export");
      setExportMsg(`Exported: ${data.gold_count} gold, ${data.bad_count} bad`);
    } catch {
      setExportMsg("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const uniqueAccounts = [...new Set(reveals.map((r) => r.account_name).filter(Boolean))].sort();

  return (
    <div className="min-h-screen bg-zinc-50 font-sans">
      {/* Top bar */}
      <div className="border-b border-zinc-200 bg-white px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-zinc-900">Eval Harness</h1>
            <p className="text-xs text-zinc-400 mt-0.5">
              Browse reveals · tag as gold / bad · score cards · export
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex gap-2 text-xs">
              {["gold", "bad", "skip"].map((t) => (
                <span key={t} className={`px-2 py-0.5 rounded-full border ${TAG_COLORS[t]}`}>
                  {tagCounts[t] || 0} {t}
                </span>
              ))}
            </div>
            <Button
              data-testid="export-btn"
              size="sm"
              variant="outline"
              onClick={handleExport}
              disabled={exporting}
              className="text-xs"
            >
              {exporting ? "Exporting…" : "Export JSON"}
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-3">
        <select
          data-testid="account-filter-select"
          value={accountFilter}
          onChange={(e) => setAccountFilter(e.target.value)}
          className="text-sm border border-zinc-200 rounded-lg px-3 py-1.5 text-zinc-700 bg-white focus:outline-none focus:border-zinc-400"
        >
          <option value="">All accounts</option>
          {uniqueAccounts.map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
        {exportMsg && (
          <span className="text-xs text-emerald-600 font-medium">{exportMsg}</span>
        )}
      </div>

      {/* Reveals table */}
      <div className="max-w-5xl mx-auto px-6 pb-12">
        {loading ? (
          <p className="text-sm text-zinc-400 py-8 text-center">Loading…</p>
        ) : reveals.length === 0 ? (
          <p className="text-sm text-zinc-400 py-8 text-center">No reveals found.</p>
        ) : (
          <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-100 text-left">
                  <th className="px-4 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wide">Account</th>
                  <th className="px-4 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wide">Cycle</th>
                  <th className="px-4 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wide">Generated</th>
                  <th className="px-4 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wide">Tag</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {reveals.map((r) => (
                  <tr
                    key={r.reveal_id}
                    data-testid={`reveal-row-${r.reveal_id}`}
                    className="border-b border-zinc-50 hover:bg-zinc-50 transition-colors"
                  >
                    <td className="px-4 py-3 font-medium text-zinc-900">{r.account_name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-500">{r.generation_cycle}</td>
                    <td className="px-4 py-3 text-xs text-zinc-400">
                      {r.generated_at ? new Date(r.generated_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-3">
                      {r.tag ? (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${TAG_COLORS[r.tag]}`}>
                          {r.tag}
                        </span>
                      ) : (
                        <span className="text-xs text-zinc-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        data-testid={`open-reveal-${r.reveal_id}`}
                        onClick={() => setSelectedReveal(r)}
                        className="text-xs text-zinc-500 hover:text-zinc-900 font-medium underline underline-offset-2"
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail overlay */}
      {selectedReveal && (
        <RevealDetailPanel
          revealId={selectedReveal.reveal_id}
          accountName={selectedReveal.account_name}
          generationCycle={selectedReveal.generation_cycle}
          onClose={() => setSelectedReveal(null)}
          onTagged={handleTagged}
        />
      )}
    </div>
  );
}
